// EPS-4414: pre-existing @typescript-eslint/no-explicit-any, no-unsafe-*, and no-floating-promises
// violations (the shared compileLazyModule helper used by every brand case, not just the new
// HSP one) unrelated to this ticket — CI's changed-files lint gate surfaces the whole file once
// any line changes. Out of scope here — see concerns.md.
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-floating-promises */
import { isPlatformServer } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ApplicationRef,
  Compiler,
  Component,
  inject,
  Inject,
  InjectionToken,
  Injector,
  Optional,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Response } from 'express';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { EmbedStylesService } from '@core-mkt/services/embed-styles/embed-styles.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { cloneDeep } from 'lodash-es';
import { filter, first } from 'rxjs';
import { RESPONSE } from '../../../../app/tokens/response.token';

@Component({
  selector: 'template-404',
  templateUrl: './template-404.component.html',
  standalone: true,
})
export class Template404Component implements OnInit {
  @ViewChild('head', { read: ViewContainerRef })
  public head: ViewContainerRef;
  @ViewChild('foot', { read: ViewContainerRef })
  public foot: ViewContainerRef;
  @ViewChild('notFound', { read: ViewContainerRef })
  public notFound: ViewContainerRef;
  resolverData: PageDataResponse;
  pageData: any;
  includeOptimizely: boolean;
  private readonly schemaService = inject(SchemaService);
  private readonly appRef = inject(ApplicationRef);

  constructor(
    private route: ActivatedRoute,
    private embedStylesService: EmbedStylesService,
    private env: EnvService,
    private compiler: Compiler,
    private injector: Injector,
    @Optional() @Inject(RESPONSE) private response: Response,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<any>,
  ) {
    if (this.route.snapshot.data.pageData) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData);
      this.pageData = this.resolverData.craftData;
      if (this.pageData.styles && this.pageData.styles.embedStyles) {
        this.embedStylesService.addEmbedStyles(this.pageData.styles.embedStyles);
      }
    }
    this.includeOptimizely = false;
  }

  async ngOnInit(): Promise<void> {
    if (isPlatformServer(this.platformId) && this.response) {
      this.response.statusCode = 404;
      this.response.statusMessage = '404 - Page Not Found';
    }
    const brand = this.env.get.brandConfig.id;
    switch (brand) {
      case 'AA': {
        await import(
          /* webpackChunkName: "aa-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        ).then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'));
        await import(
          /* webpackChunkName: "aa-header-module" */
          '@core-mkt/brands/aa/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "aa-footer-module" */
          '@core-mkt/brands/aa/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "aa-not-found-module" */
          '@core-mkt/brands/aa/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'ACE': {
        await import(
          /* webpackChunkName: "ace-banner-module" */
          '@core-mkt/shared/components/banner/banner.module'
        ).then(({ BannerModule }) => this.compileLazyModule(BannerModule, 'head'));
        await import(
          /* webpackChunkName: "ace-header-module" */
          '@core-mkt/brands/ace/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "ace-footer-module" */
          '@core-mkt/brands/ace/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "ace-not-found-module" */
          '@core-mkt/brands/ace/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'AARP': {
        await import(
          /* webpackChunkName: "aarp-header-module" */
          '@core-mkt/brands/aarp/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "aarp-footer-module" */
          '@core-mkt/brands/aarp/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "aarp-not-found-module" */
          '@core-mkt/brands/aarp/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'DEC': {
        await import(
          /* webpackChunkName: "dec-header-module" */
          '@core-mkt/brands/dec/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "dec-footer-module" */
          '@core-mkt/brands/dec/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "dec-not-found-module" */
          '@core-mkt/brands/dec/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'INS': {
        await import(
          /* webpackChunkName: "ins-header-module" */
          '@core-mkt/brands/ins/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "ins-footer-module" */
          '@core-mkt/brands/ins/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "ins-not-found-module" */
          '@core-mkt/brands/ins/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'QA': {
        await import(
          /* webpackChunkName: "qq-header-module" */
          '@core-mkt/brands/qa/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "qa-footer-module" */
          '@core-mkt/brands/qa/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "qa-not-found-module" */
          '@core-mkt/brands/qa/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'MTG': {
        await import(
          /* webpackChunkName: "mtg-header-module" */
          '@core-mkt/brands/mtg/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "mtg-footer-module" */
          '@core-mkt/brands/mtg/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "mtg-not-found-module" */
          '@core-mkt/brands/mtg/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'HSP': {
        await import(
          /* webpackChunkName: "hsp-header-module" */
          '@core-mkt/brands/hsp/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "hsp-footer-module" */
          '@core-mkt/brands/hsp/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "hsp-not-found-module" */
          '@core-mkt/brands/hsp/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }

      case 'IDS': {
        await import(
          /* webpackChunkName: "ids-header-module" */
          '@core-mkt/brands/ids/src/app/components/header/header.module'
        ).then(({ HeaderModule }) => this.compileLazyModule(HeaderModule, 'head'));
        await import(
          /* webpackChunkName: "ids-footer-module" */
          '@core-mkt/brands/ids/src/app/components/footer/footer.module'
        ).then(({ FooterModule }) => this.compileLazyModule(FooterModule, 'foot'));
        await import(
          /* webpackChunkName: "ids-not-found-module" */
          '@core-mkt/brands/ids/src/app/components/not-found/not-found.module'
        ).then(({ NotFoundModule }) => this.compileLazyModule(NotFoundModule, 'notFound'));
        break;
      }
    }

    // Flush any Schema.org fragments the PageDataResolver registered during SSR.
    // TemplateDynamicComponent renders either <template-single> or this 404
    // component; only template-single finalizes, so without this any nodes
    // registered before a 404 render would never reach <head>. No Organization
    // fallback here — an error page should emit only what was genuinely
    // registered (a no-op when nothing was). On the browser SchemaService is a
    // no-op, so this is SSR-only in practice.
    //
    // Gate on appRef.isStable (matching TemplateSingleComponent) so finalize runs
    // after lazy-loaded children's registration hooks — not synchronously in
    // ngOnInit, which would fire before those hooks and miss their fragments.
    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
      )
      .subscribe(() => {
        this.schemaService.finalize();
      });
  }

  private compileLazyModule(module, target: string): void {
    this.compiler.compileModuleAsync(module).then((moduleFactory) => {
      // Create a moduleRef, resolve an entry component, create the component
      const moduleRef = moduleFactory.create(this.injector);
      const componentFactory = (moduleRef.instance as any).resolveComponent();
      if (target === 'head') {
        this.head.createComponent(componentFactory);
      } else if (target === 'foot') {
        this.foot.createComponent(componentFactory);
      } else {
        this.notFound.createComponent(componentFactory);
      }
    });
  }
}
